/**
 * waap-threat-investigation-board.tsx — Threat Investigation analytics board.
 *
 * Distinct from the other 6 boards: full left-rail filter sidebar (15 multi-
 * select fields + time range + domain + origin-response-time slider), Apply /
 * Clear All actions, and a per-row log-detail modal. The sidebar filter options
 * are fetched server-side and update when range/domain changes.
 *
 * Mirrors Partner's ThreatInvestigationTab.tsx — ops delta: uses anchorId
 * instead of useWaapWebsite() context; replaces CSS modules with inline style.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, RotateCw } from 'lucide-react';
import {
  Button,
  MultiSelect,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  THREAT_TIME_RANGES, type ThreatTimeRange,
  type WaapThreatFilters,
  type WaapTableChart,
  type WaapTableRow,
  type WaapAccessLogArgs,
  useActAsWAAPThreatCharts,
  useActAsWAAPThreatFilterOptions,
  useActAsWAAPWebsites,
} from '@/api/act-as-waap';
import { WaapChartGrid } from './waap-chart-widgets';
import { WaapThreatLogDetailModal } from './waap-threat-log-detail-modal';

/* ── Filter field definitions (mirrors Partner's FIELDS const) ───────────── */

const FILTER_FIELDS = [
  { key: 'src_ip',              label: 'Source IP' },
  { key: 'upstream_addr',       label: 'Origin IP' },
  { key: 'request_uuid',        label: 'Request ID' },
  { key: 'path',                label: 'Path' },
  { key: 'attack_type',         label: 'Attack Type' },
  { key: 'action',              label: 'HTTP Method' },
  { key: 'status',              label: 'Status' },
  { key: 'user_agent',          label: 'User Agent' },
  { key: 'referer',             label: 'Referer' },
  { key: 'manual_rule_id',      label: 'Manual Rule Id' },
  { key: 'inject_payload',      label: 'Inject Payload' },
  { key: 'inject_attack_type',  label: 'Inject Attack Type' },
  { key: 'country',             label: 'Region' },
  { key: 'in_whitelist_ip',     label: 'In Whitelist IP' },
  { key: 'inject_rule_id',      label: 'Inject Rule Id' },
] as const;

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function toggleIn(arr: string[], v: string): string[] {
  return arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
}

/* ── Details log table (clickable rows → open detail modal) ─────────────── */

function DetailsLogTable({
  chart,
  onRowClick,
}: {
  chart: WaapTableChart;
  onRowClick: (row: WaapTableRow) => void;
}) {
  const cols = chart.columns ?? [];
  const rows = chart.rows ?? [];
  const columns = useMemo<ColumnDef<WaapTableRow, unknown>[]>(
    () =>
      cols.map((c) => ({
        id: c.key,
        accessorKey: c.key,
        header: c.label,
        cell: ({ row }) => String(row.original[c.key] ?? '—'),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cols.map((c) => c.key).join(',')],
  );
  if (rows.length === 0) return null;
  return (
    <div style={{ marginTop: 'var(--vn-space-4)' }}>
      <DataTable
        columns={columns}
        data={rows}
        onRowClick={onRowClick}
        dense
      />
    </div>
  );
}

/* ── Component ───────────────────────────────────────────────────────────── */

interface WaapThreatInvestigationBoardProps {
  anchorId: string;
  defaultRange?: ThreatTimeRange;
}

/**
 * Threat Investigation board — sidebar filter panel + chart grid + clickable
 * Details log table + per-row access-log detail modal. All read-only.
 */
export function WaapThreatInvestigationBoard({
  anchorId,
  defaultRange = 'Last 4 hours',
}: WaapThreatInvestigationBoardProps) {
  const { t } = useTranslation();

  // Sidebar draft state (not yet applied).
  const [dRange, setDRange]   = useState<ThreatTimeRange>(defaultRange);
  const [dDomains, setDDomains] = useState<string[]>([]);
  const [dMulti, setDMulti]   = useState<Record<string, string[]>>({});
  const [dResp, setDResp]     = useState('');

  // Applied state (drives queries).
  const [applied, setApplied] = useState<{
    range: ThreatTimeRange;
    domains: string[];
    filters: WaapThreatFilters;
  }>({ range: defaultRange, domains: [], filters: {} });

  // Modal state.
  const [detailArgs, setDetailArgs] = useState<WaapAccessLogArgs | null>(null);

  // Data queries.
  const websitesQ   = useActAsWAAPWebsites(anchorId);
  const optionsQ    = useActAsWAAPThreatFilterOptions(anchorId, applied.range, applied.domains);
  const chartsQ     = useActAsWAAPThreatCharts(anchorId, applied.range, applied.domains, applied.filters);

  const options = optionsQ.data ?? {};

  // Separate the "details" chart (clickable log table) from the rest.
  const detailsChart = useMemo(
    () => (chartsQ.data?.charts ?? []).find(
      (c): c is WaapTableChart => c.kind === 'table' && c.key === 'details',
    ),
    [chartsQ.data],
  );
  const restCharts = useMemo(
    () => (chartsQ.data?.charts ?? []).filter((c) => c !== detailsChart),
    [chartsQ.data, detailsChart],
  );

  // Apply sidebar → push to applied state.
  const apply = () => {
    const filters: WaapThreatFilters = {};
    for (const f of FILTER_FIELDS) {
      if (dMulti[f.key]?.length) filters[f.key] = dMulti[f.key];
    }
    if (dResp.trim() && dResp !== '0') filters.upstream_response_time_min = dResp.trim();
    setApplied({ range: dRange, domains: dDomains, filters });
  };

  const clearAll = () => {
    setDRange(defaultRange);
    setDDomains([]);
    setDMulti({});
    setDResp('');
    setApplied({ range: defaultRange, domains: [], filters: {} });
  };

  const openDetail = (row: WaapTableRow) => {
    const requestUuid = row.request_id != null ? String(row.request_id) : '';
    if (!requestUuid) return;
    const ts = row.timestamp_ms != null ? Number(row.timestamp_ms) : undefined;
    setDetailArgs({
      requestUuid,
      hostname: row.host != null ? String(row.host) : '',
      timestampMs: Number.isFinite(ts) ? ts : undefined,
    });
  };

  const websiteOptions = (websitesQ.data ?? []).map((w) => ({ value: w.domain, label: w.domain }));

  const fieldFilter = (key: string, label: string) => (
    <div key={key} style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
      <span style={{ fontSize: 'var(--vn-font-size-xs)', fontWeight: 500, color: 'var(--vn-fg-muted)' }}>{label}</span>
      <MultiSelect
        placeholder={`Any ${label}`}
        options={(options[key] ?? []).map((o) => ({ value: o, label: o || '(empty)' }))}
        value={dMulti[key] ?? []}
        onChange={(next) => {
          const nextSet = new Set(next);
          const cur = dMulti[key] ?? [];
          let updated = [...cur];
          for (const v of cur) if (!nextSet.has(v)) updated = toggleIn(updated, v);
          for (const v of next) if (!cur.includes(v)) updated = toggleIn(updated, v);
          setDMulti((prev) => ({ ...prev, [key]: updated }));
        }}
        emptyHint="No options"
      />
    </div>
  );

  const sidebarStyle: React.CSSProperties = {
    display: 'grid',
    gap: 'var(--vn-space-3)',
    alignContent: 'start',
    minWidth: '220px',
    maxWidth: '260px',
  };

  return (
    <div style={{ display: 'flex', gap: 'var(--vn-space-5)', alignItems: 'flex-start' }}>
      {/* ── Sidebar ────────────────────────────────────────────────────── */}
      <aside style={sidebarStyle}>
        <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 600, color: 'var(--vn-fg-default)' }}>
          {t('waap_domain.analytics_threat_filters', 'Filters')}
        </span>

        {/* Time Range */}
        <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
          <span style={{ fontSize: 'var(--vn-font-size-xs)', fontWeight: 500, color: 'var(--vn-fg-muted)' }}>
            {t('waap_domain.analytics_filter_range', 'Time Range')}
          </span>
          <Select value={dRange} onValueChange={(v) => setDRange(v as ThreatTimeRange)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {THREAT_TIME_RANGES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Domain */}
        <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
          <span style={{ fontSize: 'var(--vn-font-size-xs)', fontWeight: 500, color: 'var(--vn-fg-muted)' }}>
            {t('waap_domain.analytics_filter_domain', 'Domain')}
          </span>
          <MultiSelect
            placeholder="All domains"
            options={websiteOptions}
            value={dDomains}
            onChange={(next) => {
              const cur = dDomains;
              let updated = [...cur];
              const nextSet = new Set(next);
              for (const v of cur) if (!nextSet.has(v)) updated = toggleIn(updated, v);
              for (const v of next) if (!cur.includes(v)) updated = toggleIn(updated, v);
              setDDomains(updated);
            }}
            emptyHint="No websites"
          />
        </div>

        {/* Source IP + Origin IP (first two fields) */}
        {fieldFilter('src_ip', 'Source IP')}
        {fieldFilter('upstream_addr', 'Origin IP')}

        {/* Origin Response Time slider */}
        <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
          <span style={{ fontSize: 'var(--vn-font-size-xs)', fontWeight: 500, color: 'var(--vn-fg-muted)' }}>
            Origin Response Time{dResp && dResp !== '0' ? ` ≥ ${dResp}s` : ''}
          </span>
          <input
            type="range"
            min={0}
            max={5}
            step={0.1}
            value={dResp || '0'}
            onChange={(e) => setDResp(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>

        {/* Remaining filter fields */}
        {FILTER_FIELDS.slice(2).map((f) => fieldFilter(f.key, f.label))}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
          <Button onClick={apply}>{t('waap_domain.analytics_threat_apply', 'Apply')}</Button>
          <Button variant="secondary" onClick={clearAll}>{t('waap_domain.analytics_threat_clear', 'Clear all')}</Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => chartsQ.refetch()}
            disabled={chartsQ.isFetching}
          >
            <RotateCw size={14} style={{ marginRight: 'var(--vn-space-1)' }} />
            {t('waap_domain.refresh', 'Refresh')}
          </Button>
        </div>
      </aside>

      {/* ── Main board ─────────────────────────────────────────────────── */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {chartsQ.isLoading ? (
          <Skeleton height={480} />
        ) : chartsQ.data === null ? (
          <EmptyState
            icon={<Search size={20} />}
            title={t('waap_domain.analytics_threat_not_configured', 'Threat Investigation not configured')}
            description={t('waap_domain.analytics_not_configured_desc', 'This board is not set up for this environment yet.')}
          />
        ) : chartsQ.isError ? (
          <Alert tone="danger" title={t('waap_domain.analytics_load_error', "Couldn't load threat investigation analytics", { label: 'threat investigation' })}>
            {String((chartsQ.error as { message?: string })?.message ?? '')}
          </Alert>
        ) : (
          <>
            <WaapChartGrid charts={restCharts} />
            {detailsChart && (
              <DetailsLogTable chart={detailsChart} onRowClick={openDetail} />
            )}
          </>
        )}
      </div>

      {/* ── Log detail modal ────────────────────────────────────────────── */}
      <WaapThreatLogDetailModal
        anchorId={anchorId}
        args={detailArgs}
        open={!!detailArgs}
        onOpenChange={(o) => { if (!o) setDetailArgs(null); }}
      />
    </div>
  );
}
